import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import ResizableTable from "../common/ResizableTable";
import {
  FileEarmarkBarGraph,
  Calendar3,
  Filter,
  Search,
  ArrowRepeat,
} from "react-bootstrap-icons";
import DataApi from "../../api/dataApi";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BookPenaltyReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Main API Filters
  const [filters, setFilters] = useState({
    days: "90",
    startDate: "",
    endDate: "",
    category: "",
    searchTerm: "",
    condition_after: "",
  });

  // Local state for the search input to handle debouncing
  const [searchInput, setSearchInput] = useState("");
  
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [todayStr] = useState(new Date().toISOString().split("T")[0]);

  // Styles
  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const inputBaseStyle = {
    height: '44px',
    borderRadius: '8px',
    border: '1.5px solid #dee2e6',
    padding: '0.625rem 1rem',
    fontSize: '0.9375rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    width: '100%'
  };

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryApi = new DataApi("classification");
        const response = await categoryApi.fetchAll();
        const categoriesData = response?.data?.data || response?.data || [];
        const uniqueCategories = [...new Set(categoriesData.map(item => item.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only trigger API if the input is cleared OR has 3 or more characters
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setFilters(prev => ({
          ...prev,
          searchTerm: searchInput
        }));
        setCurrentPage(1);
      }
    }, 800); // 800ms delay before triggering search

    return () => clearTimeout(timer); // Cleanup timer if user keeps typing
  }, [searchInput]);

  // Fetch Report Data
  useEffect(() => {
    fetchReportData();
  }, [filters]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new DataApi("reports");
      const params = new URLSearchParams();

      if (filters.days === "custom") {
        params.append("days", "custom");
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);
      } else {
        params.append("days", filters.days);
      }

      if (filters.category) params.append("category", filters.category);
      if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
      if (filters.condition_after) params.append("condition_after", filters.condition_after);

      const response = await api.get(`/book-penalties?${params.toString()}`);
      setReportData(response.data.records || []);
    } catch (err) {
      console.error("Error fetching penalty report:", err);
      setError("Failed to load penalty report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field === "days" && value !== "custom" ? { startDate: "", endDate: "" } : {})
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchInput(""); // Clear the typing field
    setFilters({
      days: "90",
      startDate: "",
      endDate: "",
      category: "",
      searchTerm: "",
      condition_after: "",
    });
  };

  // Table Columns
  const columns = [
    { field: "Sr.No", label: "Sr No", width: "80px", align: "center" },
    { field: "Book Title", label: "Book Title", width: "250px" },
    { field: "Category", label: "Category", width: "150px" },
    { 
      field: "Total Penalty (₹)", 
      label: "Total Penalty (₹)", 
      width: "150px", 
      align: "center",
      render: (value) => <strong className="text-danger">₹{parseFloat(value || 0).toLocaleString()}</strong>
    },
    { 
      field: "Avg Days Overdue", 
      label: "Avg Days Overdue", 
      width: "130px", 
      align: "center",
      render: (value) => <Badge bg="secondary">{value}</Badge>
    },
    { 
      field: "Students", 
      label: "Students", 
      width: "200px",
      render: (value) => <small className="text-muted">{value}</small>
    },
    { 
      field: "Conditions", 
      label: "Conditions", 
      width: "150px",
      render: (value) => <small className="text-info">{value}</small>
    },
  ];

  // Export Functions
  const prepareDataForExport = () => {
    return reportData.map(row => ({
      "Sr No": row["Sr.No"],
      "Book Title": row["Book Title"],
      "Category": row.Category,
      "Penalty Count": row["Penalty Count"],
      "Total Penalty (₹)": `₹${parseFloat(row["Total Penalty (₹)"] || 0).toLocaleString()}`,
      "Avg Days Overdue": row["Avg Days Overdue"],
      "Students": row.Students,
      "Conditions": row.Conditions
    }));
  };

  const exportToCSV = () => {
    const data = prepareDataForExport();
    if (data.length === 0) return;
    const headers = Object.keys(data[0]).join(",");
    const csvContent = [
      headers,
      ...data.map(row => Object.values(row).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Book_Penalty_Report_${todayStr}.csv`);
    link.click();
  };

  const exportToExcel = () => {
    const data = prepareDataForExport();
    if (data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Penalty Report");
    XLSX.writeFile(workbook, `Book_Penalty_Report_${todayStr}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    doc.setFontSize(18);
    doc.text('Books Penalty Report', 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    const tableData = reportData.map(row => [
      row["Sr.No"],
      row["Book Title"],
      row.Category || '',
      row["Penalty Count"],
      `₹${parseFloat(row["Total Penalty (₹)"] || 0).toFixed(2)}`,
      row["Avg Days Overdue"],
      row.Conditions || ''
    ]);

    doc.autoTable({
      head: [['Sr', 'Book Title', 'Category', 'Penalty Count', 'Total Penalty (₹)', 'Avg Days Overdue', 'Conditions']],
      body: tableData,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [220, 53, 69] }, // Red header for penalties
      columnStyles: {
        4: { halign: 'right', fontStyle: 'bold' } // Total Penalty column
      }
    });

    doc.save(`Book_Penalty_Report_${todayStr}.pdf`);
  };

  if (loading && !reportData.length) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="danger" />
        <span className="ms-2">Loading penalty report...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100 pb-5">
      <div className="card shadow-sm border-0 mt-1 mx-2">
        {/* Header */}
        <div className="library-header border shadow-sm mt-3 mb-2 rounded mx-2">
          <Col md={6} className="d-flex align-items-center gap-3 ms-3">
            <button
              onClick={() => navigate('/reports')}
              className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            <div>
              <h4 className="mb-0 fw-bold fs-7" style={{ color: "var(--primary-color)" }}>
                📊 Books Penalty Report
              </h4>
              <p className="mb-0 text-muted small mx-3 m-1">Total Penalty Collected: ₹{reportData.reduce((sum, row) => sum + parseFloat(row["Total Penalty (₹)"] || 0), 0).toLocaleString()}</p>
            </div>
          </Col>
          <div className="library-header-right">
            <Dropdown align="end">
              <Dropdown.Toggle variant="light" size="sm">
                <i className="fa fa-download me-1" /> Export
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow-sm border-0">
                <Dropdown.Item onClick={exportToExcel}>
                  <i className="fa-solid fa-file-excel me-2 text-success" /> Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={exportToCSV}>
                  <i className="fa-solid fa-file-csv me-2 text-info" /> CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={exportToPDF}>
                  <i className="fa-solid fa-file-pdf me-2 text-danger" /> PDF
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Filters */}
        <div className="p-3 bg-white">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={2}>
              <div style={labelStyle}><Calendar3 /> Time Period</div>
              <Form.Select style={inputBaseStyle} value={filters.days} onChange={(e) => handleFilterChange("days", e.target.value)}>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="365">Last Year</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
            </Col>
            {filters.days === "custom" && (
              <>
                <Col xs={12} md={2}>
                  <div style={labelStyle}><Calendar3 /> From</div>
                  <Form.Control type="date" style={inputBaseStyle} value={filters.startDate} onChange={(e) => handleFilterChange("startDate", e.target.value)} />
                </Col>
                <Col xs={12} md={2}>
                  <div style={labelStyle}><Calendar3 /> To</div>
                  <Form.Control type="date" style={inputBaseStyle} value={filters.endDate} onChange={(e) => handleFilterChange("endDate", e.target.value)} />
                </Col>
              </>
            )}
            <Col xs={12} md={2}>
              <div style={labelStyle}><Filter /> Category</div>
              <Form.Select style={inputBaseStyle} value={filters.category} onChange={(e) => handleFilterChange("category", e.target.value)}>
                <option value="">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <div style={labelStyle}>
                <Search /> Search 
                {/* Optional: Small loading indicator while typing/waiting for debounce */}
                {searchInput !== filters.searchTerm && searchInput.length >= 3 && (
                  <Spinner animation="border" size="sm" className="ms-2 text-primary" />
                )}
              </div>
              <Form.Control 
                style={inputBaseStyle} 
                placeholder="Name/Book (Min 3 chars)" 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)} 
              />
            </Col>
            <Col xs={12} md={1}>
              <div style={labelStyle}><Filter className="me-1" /> Condition</div>
              <Form.Select style={inputBaseStyle} value={filters.condition_after} onChange={(e) => handleFilterChange("condition_after", e.target.value)}>
                <option value="">All</option>
                <option value="late">Late</option>
                <option value="lost">Lost</option>
                <option value="damaged">Damaged</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button variant="light" onClick={resetFilters} className="border shadow-sm" style={{ height: '44px' }}>
                  <i className="fa-solid fa-undo text-secondary" /> 
              </Button>
            </Col>
          </Row>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="danger" className="mx-3 mt-3">
            {error}
          </Alert>
        )}

        {/* Table */}
        <ResizableTable
          data={reportData}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          recordsPerPage={15}
          onPageChange={setCurrentPage}
          showSerialNumber={false}
          showActions={false}
          emptyMessage="No penalty records found. Try adjusting filters."
          footerSummary={reportData.length > 0 && (
            <div className="p-3 bg-light border-top">
              <strong>Total Books with Penalties: {reportData.length}</strong> | 
              <strong className="text-danger ms-2"> Grand Total Penalty: ₹{reportData.reduce((sum, row) => sum + parseFloat(row["Total Penalty (₹)"] || 0), 0).toLocaleString()}</strong>
            </div>
          )}
        />

        {/* Loading Overlay */}
        {loading && reportData.length > 0 && (
          <div className="position-fixed top-50 start-50 translate-middle z-index-1050">
            <Spinner animation="border" variant="danger" />
            <div className="mt-2 text-danger fw-bold">Loading penalty data...</div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BookPenaltyReport;